/**
 * @file useGdprConsentContext.tsx
 *
 * @fileoverview Custom hook that handles GDPR functionality and
 *  provides related data.
 */
import { useLazyQuery, useMutation } from '@apollo/client';
import { createContext } from 'react';
import { useState } from 'react';
import { useContext } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import * as Yup from 'yup';
import { ContentErrorList } from '~/jobiqo/components/ContentErrorList';
import CREATE_GDPR_USER_CONSENT from '~/jobiqo/components/GDPRConsent/graphql/mutations/createGdprUserConsent.gql';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import {
  GdprConsentInfo,
  GdprConsentPlacement,
  GdprUserConsent,
} from '~/jobiqo/types/types';
import { AppContextInterface, PathContextInterface } from '~/jobiqo/models/app';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import GET_GDPR_CONSENT_INFOS from '~/jobiqo/components/GDPRConsent/graphql/queries/getGdprConsentInfos.gql';
import GET_GDPR_USER_CONSENTS from '~/jobiqo/components/GDPRConsent/graphql/queries/getGdprUserConsents.gql';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface GdprFormSchema {
  schema: any;
  initialValues: Object;
}

export interface GdprCosentContextType {
  /**
   * GdprFormSchema for GDPR consents received from API.
   */
  consentsValidation: GdprFormSchema;
  /**
   * Runs mutation to create GDPR user consent.
   */
  createGdprUserConsent: (mail: string) => void;
  /**
   * Error list node with mutation's errors data.
   */
  errorList: React.ReactNode;
  /**
   * Consent Infos received from the API.
   */
  consentInfos: Array<GdprConsentInfo>;
  /**
   * Consent Infos received from the API.
   */
  requiredConsentInfos: Array<GdprConsentInfo>;
  /*
   * Intl shape.
   */
  intl: IntlShape;
  /**
   * Gets form field name for GdprConsentInfo.
   */
  getTermFormFieldName: (gdprConsentInfo: GdprConsentInfo) => string;
  /**
   * Flag indicating if module is disabled.
   */
  isModuleDisabled: boolean;
  /**
   * Flag indicating if newsletter checkbox is checked.
   */
  isNewsletterChecked: boolean;
  /**
   * Allows setting isNewsletterChecked
   */
  setIsNewsletterChecked: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Flag indicating if GDPR consents are available.
   * Used in components that have to wait for consents data to
   * enable form submission.
   */
  consentsAvailable?: boolean;
  /**
   * Queries GDPR consents data.
   * Called by the components that render GDPR consents.
   */
  queryGdprUserConsent: (consentPlacement: GdprConsentPlacement) => void;
  /**
   * Queries user GDPR consents data.
   */
  queryGdprUserConsents: (
    mail: string,
    placement: GdprConsentPlacement,
  ) => void;
  /**
   * User GDPR consents.
   */
  gdprUserConsents: Array<GdprUserConsent>;
  /**
   * Consent Infos that the user agreed with.
   */
  consentsChecked: Array<number>;
  /**
   * Toggle a consent.
   */
  consentToggle: (id: number) => void;
}

const GdprConsentContext = createContext<GdprCosentContextType | null>(null);

export const GdprConsentProvider = ({ children }) => {
  const intl = useIntl();
  const { APP } = useFrontendConfig();
  const GDPR_CONSENT_FEATURE_ACTIVE = APP.GDPR_CONSENT_FEATURE_ACTIVE;

  const { user }: UserContextInterface = useContext(UserContext);
  const { locale }: AppContextInterface = useContext(AppContext);
  const { path }: PathContextInterface = useContext(PathContext);

  // If the feature is not active, set the flag to true so that components that are using the flag can continue.
  const [consentsAvailable, setConsentsAvailable] = useState(
    !GDPR_CONSENT_FEATURE_ACTIVE,
  );
  const [consentsValidation, setConsentsValidation] = useState<GdprFormSchema>({
    schema: {},
    initialValues: {},
  });
  // List of consents that exist in the backend (all consents).
  const [consentInfos, setConsentInfos] = useState<Array<GdprConsentInfo>>([]);

  // List of consents the user agreed with.
  const [consentsChecked, setConsentsChecked] = useState<Array<number>>([]);

  const [requiredConsentInfos, setRequiredConsentInfos] = useState<
    Array<GdprConsentInfo>
  >([]);
  const [isNewsletterChecked, setIsNewsletterChecked] = useState<boolean>(
    false,
  );

  let errorList: React.ReactNode = null;

  const [
    getGdprConsentInfos,
    { data: getGdprConsentInfosData, error: getGdprConsentInfosError },
  ] = useLazyQuery(GET_GDPR_CONSENT_INFOS, {
    onCompleted: ({ getGdprConsentInfos }) => {
      if (getGdprConsentInfos) {
        // First set the validation.
        updateConsentsValidation(getGdprConsentInfos);
        // Set the consents so that the components using them can be rendered.
        setConsentInfos(getGdprConsentInfos);
        // Update flag.
        setConsentsAvailable(true);
      }
    },
    fetchPolicy: 'no-cache',
  });

  if (getGdprConsentInfosError) {
    errorList = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="get_gdpr_consent_infos_error"
          defaultMessage="Error getting GDPR consent informations."
        />
      </MessageAlert>
    );
  }

  if (getGdprConsentInfosData?.getGdprConsentInfos.errors?.length > 0) {
    errorList = (
      <ContentErrorList
        errors={getGdprConsentInfosData.getGdprConsentInfos.errors}
      />
    );
  }

  const [gdprUserConsents, setGdprUserConsents] = useState<
    Array<GdprUserConsent>
  >([]);
  const [
    getGdprUserConsents,
    { data: getGdprUserConsentsData, error: getGdprUserConsentsError },
  ] = useLazyQuery(GET_GDPR_USER_CONSENTS, {
    fetchPolicy: 'no-cache',
    onCompleted: ({ getGdprUserConsents }) =>
      !getGdprUserConsents.errors?.length &&
      setGdprUserConsents(getGdprUserConsents),
  });

  if (getGdprUserConsentsError) {
    errorList = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="get_gdpr_user_consents_error"
          defaultMessage="Error getting user GDPR consents."
        />
      </MessageAlert>
    );
  }

  if (getGdprUserConsentsData?.getGdprUserConsents.errors?.length > 0) {
    errorList = (
      <ContentErrorList
        errors={getGdprUserConsentsData.getGdprUserConsents.errors}
      />
    );
  }

  const queryGdprUserConsent = (consentPlacement: GdprConsentPlacement) =>
    GDPR_CONSENT_FEATURE_ACTIVE &&
    getGdprConsentInfos({
      variables: {
        placement: consentPlacement,
        language: locale,
        path: path,
      },
    });

  const [
    createGdprUserConsent,
    { data: createGdprUserConsentData, error: createGdprUserConsentError },
  ] = useMutation(CREATE_GDPR_USER_CONSENT);

  if (createGdprUserConsentError) {
    errorList = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="create_gdpr_user_consent_error"
          defaultMessage="Error creating GDPR consent."
        />
      </MessageAlert>
    );
  }

  if (createGdprUserConsentData?.createGdprUserConsent.errors?.length > 0) {
    errorList = (
      <ContentErrorList
        errors={createGdprUserConsentData?.createGdprUserConsent.errors}
      />
    );
  }

  // Run mutation only if module is enabled.
  const handleCreateGdprUserConsent = (mail: string) =>
    GDPR_CONSENT_FEATURE_ACTIVE &&
    consentsChecked.forEach((id: number) => {
      createGdprUserConsent({
        variables: {
          mail: mail ?? user?.email,
          infoId: id,
        },
      });
    });

  const updateConsentsValidation = (consentInfos: GdprConsentInfo[]) => {
    const schema = {};
    const initialValues = {};

    consentInfos.forEach((consentInfo: GdprConsentInfo) => {
      if (consentInfo.optIn) {
        // Create schema for received consent infos.
        if (consentInfo.type !== 'NEWSLETTER') {
          schema[getTermFormFieldName(consentInfo)] = Yup.bool()
            .required(
              intl.formatMessage({
                id: 'terms_accept_error',
                defaultMessage: 'Please accept the terms and conditions',
              }),
            )
            .oneOf(
              [true],
              intl.formatMessage({
                id: 'terms_accept_error',
                defaultMessage: 'Please accept the terms and conditions',
              }),
            );

          setRequiredConsentInfos((state) => [...state, consentInfo]);
        }
        // Populate initial values for received consent infos.
        initialValues[getTermFormFieldName(consentInfo)] = false;
      }
    });

    setConsentsValidation({ schema, initialValues });
  };

  const consentToggle = (id: number) => {
    if (consentsChecked.includes(id)) {
      // Remove.
      setConsentsChecked(
        consentsChecked.filter((consentChecked) => consentChecked !== id),
      );
    } else {
      // Add.
      setConsentsChecked([...consentsChecked, id]);
    }
  };

  const getTermFormFieldName = ({ id }: GdprConsentInfo) => `term-${id}`;

  return (
    <GdprConsentContext.Provider
      value={{
        consentsValidation,
        errorList,
        consentInfos,
        requiredConsentInfos,
        gdprUserConsents,
        intl,
        isNewsletterChecked,
        consentsAvailable,
        isModuleDisabled: !GDPR_CONSENT_FEATURE_ACTIVE,
        createGdprUserConsent: handleCreateGdprUserConsent,
        getTermFormFieldName,
        setIsNewsletterChecked,
        consentsChecked,
        consentToggle,
        queryGdprUserConsent,
        queryGdprUserConsents: (
          mail: string,
          placement: GdprConsentPlacement,
        ) => getGdprUserConsents({ variables: { mail, placement } }),
      }}
    >
      {children}
    </GdprConsentContext.Provider>
  );
};

export const useGdprConsentContext = (): GdprCosentContextType =>
  useContext(GdprConsentContext) as GdprCosentContextType;
